import { ArrowLeftIcon, UserPlus2Icon } from "lucide-react"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { AppDateInput } from "@/components/commons"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { ComboBox } from "@/components/ui/combo-box"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useSidebar } from "@/components/ui/sidebar"
import { FRONTEND_ROUTES } from "@/constants"
import { formatDate } from "@/helpers/format"
export const OrdersCreateOrdersForm = () => {
  const { t } = useTranslation()
  const { open } = useSidebar()
  const form = useForm()
  const [customerChecked, setCustomerChecked] =
    useState(false)
  const [isToday, setIsToday] = useState(false)
  const today = new Date()
  const handleToggleIsTodayCheckbox = (
    v: boolean | "indeterminate"
  ) => {
    setIsToday(Boolean(v))
    if (v) {
      form.setValue("start_date", new Date())
    } else {
      form.setValue("start_date", null)
    }
  }
  const paymentMethodItems = [
    {
      label: t("orders.createOrder.creditCard"),
      value: "credit_card",
      description:
        "1,99% a.vista / 2-6x 2,39% / 7-12 2,79%",
    },
    {
      label: t("orders.createOrder.pix"),
      value: "pix",
      description: "R$ 0,25 por pix recebido",
    },
    {
      label: t("orders.createOrder.pixOpenFinance"),
      value: "pix_open_finance",
      description: "R$ 0,18 por pix recebido",
    },
    {
      label: t("orders.createOrder.bankSlip"),
      value: "boleto",
      description:
        "1,99% a.vista / 2-6x 2,39% / 7-12 2,79%",
    },
    {
      label: t("orders.createOrder.nupay"),
      value: "nupay",
      description:
        "1,99% a.vista / 2-6x 2,39% / 7-12 2,79%",
    },
    {
      label: t("orders.createOrder.liveloPoints"),
      value: "pontos_livelo",
      description:
        "1,99% a.vista / 2-6x 2,39% / 7-12 2,79%",
    },
  ]
  return (
    <Form {...form}>
      <form
        action=""
        className="group space-y-4"
        data-sidebar-open={open}
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <FormField
            control={form.control}
            name="customer"
            render={() => (
              <FormItem>
                <FormLabel>
                  {t("orders.createOrder.customer")}
                </FormLabel>
                <FormControl>
                  <ComboBox
                    items={[]}
                    onSelect={() => {}}
                    selected={null}
                    placeholder={t(
                      "orders.createOrder.selectCustomer"
                    )}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div
            className="flex items-end justify-end data-[has-error=true]:mb-7 sm:col-span-2 lg:col-span-1 lg:justify-start"
            data-has-error={form.formState.errors}
          >
            <Button
              type="button"
              variant="link"
              className="text-zinc-950"
            >
              {t("orders.createOrder.addProduct")}
            </Button>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 group-data-[sidebar-open=true]:lg:grid-cols-2">
          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("orders.createOrder.amount")}
                </FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("orders.createOrder.description")}
                </FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div
          data-has-error={form.formState.errors}
          className="grid items-end gap-4 sm:grid-cols-2 group-data-[sidebar-open=false]:lg:grid-cols-4 xl:grid-cols-4"
        >
          <FormField
            control={form.control}
            name="amount_installments"
            render={() => (
              <FormItem>
                <FormLabel>
                  {t("orders.createOrder.period")}
                </FormLabel>
                <FormControl>
                  <ComboBox
                    items={[]}
                    onSelect={() => {}}
                    selected={null}
                    placeholder={t(
                      "orders.createOrder.selectPeriod"
                    )}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="start_date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("orders.createOrder.firstCharge")}
                </FormLabel>
                <FormControl>
                  <AppDateInput
                    disabledField={isToday}
                    onSelect={() => {}}
                    fromDate={new Date()}
                    selected={field.value}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex h-10 items-center gap-2">
            <Checkbox
              id="is-today"
              checked={isToday}
              onCheckedChange={(v) =>
                handleToggleIsTodayCheckbox(v)
              }
            />
            <Label htmlFor="is-today">
              {t("orders.createOrder.today")} -{" "}
              {formatDate({ date: today })}
            </Label>
          </div>
        </div>
        <div className="flex flex-col items-start justify-between gap-4 lg:flex-col group-data-[sidebar-open=true]:lg:flex-col xl:flex-row">
          <FormField
            control={form.control}
            name="paymentMethods"
            render={() => (
              <FormItem className="w-full">
                <div className="grid gap-4 md:grid-cols-[18rem_1fr]">
                  <FormLabel>
                    {t("orders.createOrder.paymentMethods")}
                  </FormLabel>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="customer-checked"
                      checked={customerChecked}
                      onCheckedChange={() =>
                        setCustomerChecked(!customerChecked)
                      }
                    />
                    <Label htmlFor="customer-checked">
                      {t(
                        "orders.createOrder.customerChooses"
                      )}
                    </Label>
                  </div>
                </div>
                <Card className="w-full max-w-2xl p-0">
                  <CardContent className="flex w-full flex-col gap-4 p-4">
                    {paymentMethodItems.map((item) => (
                      <FormField
                        key={item.value}
                        control={form.control}
                        name="paymentMethods"
                        render={() => {
                          return (
                            <FormItem key={item.value}>
                              <div className="flex flex-col max-md:gap-2 md:grid md:grid-cols-[18rem_1fr]">
                                <FormLabel>
                                  {item.label}
                                </FormLabel>
                                <div className="flex items-start gap-2 space-y-0 md:items-center">
                                  <FormControl>
                                    <Checkbox
                                      disabled={
                                        customerChecked
                                      }
                                    />
                                  </FormControl>
                                  <p className="text-sm">
                                    {item.description}
                                  </p>
                                </div>
                              </div>
                            </FormItem>
                          )
                        }}
                      />
                    ))}
                  </CardContent>
                </Card>{" "}
                <div className="mt-16 flex flex-col-reverse items-center justify-end gap-4 sm:flex-row">
                  <Button
                    type="button"
                    asChild
                    variant="outline"
                    className="max-sm:w-full sm:min-w-40"
                  >
                    <Link
                      to={
                        FRONTEND_ROUTES.SUBSCRIPTIONS.href
                      }
                    >
                      <ArrowLeftIcon
                        size={18}
                        className="mr-2"
                      />
                      <span>{t("common.cancel")}</span>
                    </Link>
                  </Button>
                  <Button
                    type="submit"
                    className="max-sm:w-full sm:min-w-40"
                  >
                    <UserPlus2Icon
                      size={18}
                      className="mr-2"
                    />
                    <span>{t("common.create")}</span>
                  </Button>
                </div>
              </FormItem>
            )}
          />
        </div>
      </form>
    </Form>
  )
}
